import { fg } from '@atlaskit/platform-feature-flags';
import {
	buildAtlAttributionHeaders,
	getOwners,
	smartsRecommendedUserQuery,
	type User,
	usersQuery,
} from '@atlassian/search-client';

import { type CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type { SelectFilterState } from '../../../../../common/constants/filters/select-filter/types';
import {
	ProductKeys,
	type ProductKeys1P,
	type ProductKeys3P,
} from '../../../../../common/constants/products';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

const MAX_SMART_DEFAULT_OPTIONS = 5;
const MAX_SEARCHED_OPTIONS_RESULTS = 5;

const loadRecommendedOwner = async (config: CloudConfig) => {
	const headers = buildAtlAttributionHeaders({
		cloudId: config.cloudId,
		activationId: config.activationId,
		product: config.primaryProduct,
	});
	const initialUsersQuery = usersQuery(
		[
			...(config.userId ? [config.userId] : []), // The current user should always be the first recommended user
		],
		headers,
	);

	const recommendedUserQuery = smartsRecommendedUserQuery({
		aggAbsoluteUrl: config.aggAbsoluteUrl,
		variables: {
			userId: config.userId || '',
			tenantId: config.cloudId || '',
			maxNumberOfResults: MAX_SMART_DEFAULT_OPTIONS,
		},
		headers,
	});

	const [initialUsersResponse, smartsRecommendedUserResponse] = await Promise.all([
		initialUsersQuery,
		recommendedUserQuery,
	]);

	const recommendedUsersResults = smartsRecommendedUserResponse.data?.smarts.results ?? [];

	const allRecommendedUsers = [
		...(initialUsersResponse.data?.users ?? []),
		...recommendedUsersResults.flatMap((recommendedUsers) =>
			!!recommendedUsers && !!recommendedUsers.user
				? { id: recommendedUsers.id, ...recommendedUsers.user }
				: [],
		),
	] satisfies User[];

	const recommendedUserOptions = allRecommendedUsers.map((user) => ({
		trackingKey: user.accountId,
		value: user.accountId,
		label: user.name,
		avatarUrl: user.picture,
		queryParamValue: user.accountId,
		product: config.product,
	}));

	return recommendedUserOptions;
};

const productKeys1P: ProductKeys1P[] = [ProductKeys.Confluence, ProductKeys.Focus];

const products3PFromConfig: ProductKeys3P[] = getProducts3PFromConfigForFilter('owner');

export const filter: Readonly<{
	id: 'owner';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'owner'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (
		state: unknown,
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'owner'>;
	getInitialState: (
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'owner'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createMultiSelectFilter({
	id: 'owner',
	products: [...productKeys1P, ...products3PFromConfig],
	alwaysVisible: true,
	universal: false,
	queryParams: {
		key: 'owners',
		fetcher: async (queryParams, config) => {
			const headers = buildAtlAttributionHeaders({
				cloudId: config.cloudId,
				activationId: config.activationId,
				product: config.primaryProduct,
			});
			const initialUsersReponse = await usersQuery([...queryParams], headers);

			const allRecommendedUsers = initialUsersReponse.data?.users ?? ([] satisfies User[]);

			const recommendedUserOptions = allRecommendedUsers.map((user) => ({
				trackingKey: user.accountId,
				value: user.accountId,
				label: user.name,
				avatarUrl: user.picture,
				queryParamValue: user.accountId,
				product: config.product,
			}));

			return recommendedUserOptions;
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return loadRecommendedOwner(config);
		},
	},
	lookup: {
		fetcher: async (query, config) => {
			if (!config.intl) {
				return [];
			}

			const response = await getOwners({
				query,
				cloudId: config.cloudId || '',
				userId: config.userId || '',
				productKey: fg('search_page_filter_product_key')
					? (config.primaryProduct || ProductKeys.Confluence)
					: ProductKeys.Confluence,
				maxNumberOfResults: MAX_SEARCHED_OPTIONS_RESULTS,
				intl: config.intl,
			});

			if (!response) {
				// TODO: log not found somewhere
				return [];
			}

			return response.map((owner) => ({
				trackingKey: owner.id,
				value: owner.id,
				label: owner.name,
				avatarUrl: owner.avatarUrl,
				queryParamValue: owner.id,
				product: config.product,
			}));
		},
	},
});
